import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Check, Loader2, MessageCircle } from "lucide-react";
import { http, brl } from "@/lib/api";
import { Logo } from "@/components/site/Logo";

const STEPS = [
  ["pago", "Pagamento confirmado", "Recebemos o seu pagamento."],
  ["fila", "Na fila", "Aguarde ser chamado(a) no WhatsApp, por ordem de agendamento."],
  ["atendimento", "Em atendimento", "As cartas estão sendo abertas para você."],
  ["concluido", "Concluído", "Sua leitura foi entregue. Desde já, grato."],
];
const ORDER = { fila: 1, atendimento: 2, concluido: 3 };

const Step = ({ s, i, current, position }) => {
  const done = i < current;
  const active = i === current;
  return (
    <motion.li initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}
      data-testid={`status-step-${s[0]}`} className="relative flex gap-5 pb-8 last:pb-0">
      {i < STEPS.length - 1 && <span className={`absolute left-[15px] top-8 h-full w-px ${done ? "bg-[#CBA365]" : "bg-white/15"}`} />}
      <span className={`relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full border ${done ? "border-[#CBA365] bg-[#CBA365] text-[#080312]" : active ? "border-[#CBA365] text-[#CBA365] shadow-[0_0_24px_rgba(203,163,101,0.5)]" : "border-white/20 text-white/40"}`}>
        {done ? <Check className="h-4 w-4" /> : <span className="text-xs">{i + 1}</span>}
      </span>
      <div>
        <p className={`font-display text-2xl ${active || done ? "text-[#F5F2EB]" : "text-white/50"}`}>{s[1]}</p>
        <p className="text-sm text-white/65">{s[2]}</p>
        {active && s[0] === "fila" && position && (
          <p data-testid="status-queue-position" className="mt-2 text-sm text-[#CBA365]">Sua posição na fila: {position}º</p>
        )}
      </div>
    </motion.li>
  );
};

export default function OrderStatus() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    const load = () => http.get(`/orders/${id}`).then(({ data }) => setOrder(data)).catch(() => setError(true));
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [id]);

  const paid = order?.status === "approved";
  const current = paid ? ORDER[order?.service_status] ?? 1 : 0;

  return (
    <div className="min-h-screen">
      <header className="border-b border-white/10 px-5 py-5 md:px-10"><Logo /></header>
      <div data-testid="order-status-page" className="mx-auto mt-12 max-w-lg rounded-2xl border border-[#CBA365]/30 bg-[#0d0519] px-6 py-10 md:px-10">
        <p className="text-xs uppercase tracking-[0.3em] text-[#CBA365]">Meu pedido</p>
        {error && <p className="font-display mt-4 text-3xl">Pedido não encontrado</p>}
        {!order && !error && <Loader2 className="mt-6 h-8 w-8 animate-spin text-[#CBA365]" />}
        {order && (<>
          <h1 className="font-display mt-3 text-4xl">{order.plan_label} <span className="gold-text">· {brl(order.amount)}</span></h1>
          <p className="mt-1 text-sm text-white/60">Pedido #{order.id?.slice(0, 8).toUpperCase()} · {order.name}</p>
          {paid ? (
            <ol className="mt-10">{STEPS.map((s, i) => <Step key={s[0]} s={s} i={i} current={current} position={order.queue_position} />)}</ol>
          ) : (
            <p data-testid="status-unpaid" className="mt-8 rounded-xl border border-white/15 p-4 text-sm text-white/75">
              {["rejected", "cancelled", "refunded"].includes(order.status) ? "Este pagamento não foi concluído." : "Aguardando a confirmação do pagamento."}
            </p>
          )}
          {paid && order.service_status !== "concluido" && (
            <a href={order.whatsapp_url} data-testid="status-whatsapp-btn"
              className="mt-10 inline-flex items-center gap-2 rounded-full bg-[#CBA365] px-7 py-3 text-sm font-semibold uppercase tracking-[0.18em] text-[#080312] transition-colors duration-300 hover:bg-[#E5C085]">
              <MessageCircle className="h-4 w-4" /> Abrir WhatsApp
            </a>
          )}
          <p className="mt-8 text-xs text-white/55">O atendimento tem o prazo de 24 horas. Esta página atualiza sozinha.</p>
        </>)}
      </div>
    </div>
  );
}
